(ns ui.view.transaction-process.modals.decision.event
  (:require
   [common.transaction-process.hooks :as hooks]
   [common.transaction-process.templates.registry :as template-registry]
   [frontend.actions.decision :as decision-actions]
   [frontend.utils :as fu]
   [re-frame.core :as rf]
   [ui.state.session.cofx :as session-cofx]
   [ui.state.time.cofx :as time-cofx]
   [ui.view.transaction-process.modals.common :as common]
   [ui.state.named-values :as named-values]
   [ui.view.transaction-process.modals.decision.model :as model]
   [ui.view.transaction-process.model :as tp-model]
   [ui.view.transaction-process.util :as tp-util]))

(defn- active-account?
  [entities account-id]
  (let [acc (common/get-entity entities account-id)]
    (and acc (not (:common/disabled? acc)))))

(defn- default-decision-date
  [{:keys [process slot-kw fallback]}]
  (or (get process
           (case slot-kw
             :transaction-process/ic-decision
             :transaction-process/anticipated-ic-decision-date

             :transaction-process/anticipated-decision-date))
      fallback))

(defn- build-create-form
  [{:keys [template entity-type slot-kw decision-type-label default-participant-ids
           entities reservation-types default-currency today-utc-midnight-ms process]}]
  (let [entry               (tp-util/dictionary-entry template slot-kw)
        sections            (get (:template/decision-sections template) slot-kw)
        participant-entries (if (model/section-allowed? sections :participants)
                              (model/build-participant-entries
                               {:default-participant-ids default-participant-ids
                                :active-account?         #(active-account? entities %)
                                :create-temp-id          #(fu/create-temp-id "temp-participant-")})
                              [])
        ;; Fire the slot's create-form-open prefill hook generically — a slot
        ;; with no :hook/prefill (IC/AB/AIFM) is a no-op; the PM slot seeds its
        ;; reservations. The engine names no specific hook and does no type match.
        reservation-entries (vec
                             (hooks/run-hook
                              (get-in entry [:slot/hooks :hook/prefill])
                              {:process-entity    process
                               :reservation-types reservation-types
                               :create-temp-id    #(fu/create-temp-id "temp-reservation-")
                               :default-currency  default-currency}))]
    (model/init-create-state
     {:decision-id         (fu/create-temp-id "temp-decision-")
      :entity-type         entity-type
      :slot-kw             slot-kw
      :decision-type-label decision-type-label
      :participant-entries participant-entries
      :reservation-entries reservation-entries
      :default-date        (default-decision-date
                            {:process  process
                             :slot-kw  slot-kw
                             :fallback today-utc-midnight-ms})})))

(defn- build-edit-form
  [{:keys [existing-decision slot-kw decision-type-label entities]}]
  (let [decision-id     (:db/id existing-decision)
        decision-data   (common/get-entity entities decision-id)
        ref->id         (fn [ref] (or (:db/id ref) ref))
        participant-ids (mapv ref->id (:decision/participants decision-data))
        condition-ids   (mapv ref->id (:decision/conditions decision-data))
        reservation-ids (mapv ref->id (:decision/investment-reservations decision-data))
        sub-entities    (->> (concat participant-ids condition-ids reservation-ids)
                             (map (fn [id] [id (common/get-entity entities id)]))
                             (into {}))
        decision        (-> decision-data
                            (assoc :decision/participants participant-ids)
                            (assoc :decision/conditions condition-ids)
                            (assoc :decision/investment-reservations reservation-ids))
        entities        (assoc sub-entities decision-id decision)]
    (model/init-edit-state
     {:decision-id         decision-id
      :slot-kw             slot-kw
      :decision-type-label decision-type-label
      :entities            entities})))

(rf/reg-event-fx
 ::open
 [rf/trim-v
  (rf/inject-cofx ::session-cofx/session-context)
  (rf/inject-cofx ::time-cofx/today-utc-midnight-ms)]
 (fn [{:keys [db session-context today-utc-midnight-ms]}
      [{:keys [state-path process-id] :as _ctx}
       {:keys [entity-type slot-kw decision-type-label
               default-participant-ids existing-decision]}]]
   (let [{:keys [entities default-currency investment-reservation-types]} session-context
         server   (get-in db (tp-model/server-state-full-path state-path))
         template (template-registry/get-template (:transaction-process/template-kw server))
         form     (if existing-decision
                    (build-edit-form
                     {:existing-decision   existing-decision
                      :slot-kw             slot-kw
                      :decision-type-label decision-type-label
                      :entities            entities})
                    (build-create-form
                     {:process-id              process-id
                      :template                template
                      :entity-type             entity-type
                      :slot-kw                 slot-kw
                      :decision-type-label     decision-type-label
                      :default-participant-ids default-participant-ids
                      :entities                entities
                      :reservation-types       investment-reservation-types
                      :default-currency        default-currency
                      :process                 server
                      :today-utc-midnight-ms   today-utc-midnight-ms}))]
     {:db (-> db
              (assoc-in (tp-model/modal-full-path state-path)
                        {:modal/modal-type (if existing-decision :decision-edit :decision-create)})
              (assoc-in (common/form-full-path state-path) form))
      :fx [[:dispatch [::named-values/ensure-loaded :decision-participant/role]]]})))

(defn- entities-path
  [state-path]
  (common/form-key-full-path state-path :entities))

(defn- decision-id-of
  [db state-path]
  (get-in db (common/form-key-full-path state-path :decision-id)))

(rf/reg-event-db
 ::update-entity-field
 [rf/trim-v]
 (fn [db [{:keys [state-path] :as _ctx} entity-id field value]]
   (assoc-in db (into (entities-path state-path) [entity-id field]) value)))

(rf/reg-event-db
 ::add-participants
 [rf/trim-v]
 (fn [db [{:keys [state-path] :as _ctx} person-ids]]
   (let [ent-path        (entities-path state-path)
         decision-id     (decision-id-of db state-path)
         existing        (->> (get-in db (conj ent-path decision-id :decision/participants))
                              (keep (fn [pid] (get-in db (conj ent-path pid))))
                              (remove :common/disabled?))
         existing-person (->> existing
                              (map (fn [p] (let [ref (:decision-participant/person p)] (or (:db/id ref) ref))))
                              set)]
     ;; Thread an incrementing ord so each appended participant sorts after the
     ;; previous one (and after any existing participants).
     (first
      (reduce (fn [[d ord] person-id]
                (let [temp-id (fu/create-temp-id "temp-participant-")]
                  [(-> d
                       (assoc-in (conj ent-path temp-id)
                                 {:db/id                           temp-id
                                  :entity/type                     :entity.type/decision-participant
                                  :decision-participant/person     person-id
                                  :decision-participant/attendance :decision-participant.attendance/attended
                                  :common/ord                      ord})
                       (update-in (conj ent-path decision-id :decision/participants) (fnil conj []) temp-id))
                   (inc ord)]))
              [db (model/next-ord existing)]
              (remove existing-person person-ids))))))

(rf/reg-event-db
 ::remove-participant
 [rf/trim-v]
 (fn [db [{:keys [state-path] :as _ctx} pid]]
   (let [ent-path    (entities-path state-path)
         decision-id (decision-id-of db state-path)]
     (-> db
         (update-in (conj ent-path decision-id :decision/participants)
                    (fn [pids] (vec (remove #{pid} pids))))
         (assoc-in (conj ent-path pid :common/disabled?) true)))))

(rf/reg-event-db
 ::add-condition
 [rf/trim-v]
 (fn [db [{:keys [state-path] :as _ctx}]]
   (let [ent-path    (entities-path state-path)
         decision-id (decision-id-of db state-path)
         existing    (->> (get-in db (conj ent-path decision-id :decision/conditions))
                          (keep (fn [cid] (get-in db (conj ent-path cid))))
                          (remove :common/disabled?))
         temp-id     (fu/create-temp-id "temp-condition-")]
     (-> db
         (assoc-in (conj ent-path temp-id)
                   {:db/id       temp-id
                    :entity/type :entity.type/decision-condition
                    :common/ord  (model/next-ord existing)})
         (update-in (conj ent-path decision-id :decision/conditions) (fnil conj []) temp-id)))))

(rf/reg-event-db
 ::remove-condition
 [rf/trim-v]
 (fn [db [{:keys [state-path] :as _ctx} cid]]
   (let [ent-path    (entities-path state-path)
         decision-id (decision-id-of db state-path)]
     (-> db
         (update-in (conj ent-path decision-id :decision/conditions)
                    (fn [cids] (vec (remove #{cid} cids))))
         (assoc-in (conj ent-path cid :common/disabled?) true)))))

(rf/reg-event-fx
 ::add-reservation
 [rf/trim-v (rf/inject-cofx ::session-cofx/session-context)]
 (fn [{:keys [db session-context]} [{:keys [state-path] :as _ctx} type-id]]
   (let [ent-path    (entities-path state-path)
         decision-id (decision-id-of db state-path)
         existing    (->> (get-in db (conj ent-path decision-id :decision/investment-reservations))
                          (keep (fn [rid] (get-in db (conj ent-path rid))))
                          (remove :common/disabled?))
         next-ord    (model/next-ord existing)
         temp-id     (fu/create-temp-id "temp-reservation-")]
     {:db (-> db
              (assoc-in (conj ent-path temp-id)
                        {:db/id                              temp-id
                         :entity/type                        :entity.type/investment-reservation
                         :investment-reservation/type        type-id
                         :investment-reservation/amount--sym (:default-currency session-context)
                         :common/ord                         next-ord})
              (update-in (conj ent-path decision-id :decision/investment-reservations) (fnil conj []) temp-id))})))

(rf/reg-event-db
 ::remove-reservation
 [rf/trim-v]
 (fn [db [{:keys [state-path] :as _ctx} rid]]
   (let [ent-path    (entities-path state-path)
         decision-id (decision-id-of db state-path)]
     (-> db
         (update-in (conj ent-path decision-id :decision/investment-reservations)
                    (fn [rids] (vec (remove #{rid} rids))))
         (assoc-in (conj ent-path rid :common/disabled?) true)))))

(rf/reg-event-fx
 ::save
 [rf/trim-v]
 (fn [{:keys [db]} [{:keys [state-path process-id] :as ctx} draft?]]
   (let [form-path   (common/form-full-path state-path)
         decision-id (decision-id-of db state-path)
         new-db      (-> db
                         (assoc-in (into form-path [:entities decision-id :decision/draft?]) draft?)
                         (assoc-in (conj form-path :submitting?) true)
                         (assoc-in (conj form-path :error) nil))
         {:keys [entities edit-mode? original-entities slot-kw]} (get-in new-db form-path)]
     (if edit-mode?
       (let [{:keys [added-entity-ids disabled-entity-ids]}
             (model/compute-update-diff {:original-entities original-entities
                                         :entities          entities
                                         :decision-id       decision-id})
             clean-entities (->> entities
                                 (map (fn [[id e]] [id (common/serialize-entity-for-backend e)]))
                                 (into {}))]
         {:db new-db
          ::common/run-action
          {:action     (fn [success! error!]
                         (decision-actions/*->update-for-transaction-process
                          {:data       {:process-id          process-id
                                        :slot-attr           slot-kw
                                        :decision-id         decision-id
                                        :entities            clean-entities
                                        :added-entity-ids    (when (seq added-entity-ids) added-entity-ids)
                                        :disabled-entity-ids (when (seq disabled-entity-ids) disabled-entity-ids)}
                           :on-success success!
                           :on-error   error!}))
           :on-success [::save-success ctx]
           :on-error   [::save-error ctx]}})
       (let [clean-entities (->> entities
                                 (remove (fn [[_ e]] (:common/disabled? e)))
                                 (map (fn [[id e]] [id (common/serialize-entity-for-backend e)]))
                                 (into {}))]
         {:db new-db
          ::common/run-action
          {:action     (fn [success! error!]
                         (decision-actions/*->create-with-response
                          {:decision-id decision-id
                           :process-id  process-id
                           :slot-attr   slot-kw
                           :entities    clean-entities}
                          success! error!))
           :on-success [::save-success ctx]
           :on-error   [::save-error ctx]}})))))

(rf/reg-event-fx
 ::save-success
 [rf/trim-v]
 (fn [_ [ctx]]
   (common/save-success-effects ctx)))

(rf/reg-event-db
 ::save-error
 [rf/trim-v]
 (fn [db [{:keys [state-path] :as _ctx} err]]
   (common/save-error-db db
                         {:state-path state-path}
                         err
                         {:log-message     "Failed to save decision"
                          :fallback-tr-key :transaction-process/save-decision-error})))
